import React, { useEffect, useMemo, useState } from "react";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import "./Portfolio.css";

const Portfolio = ({
  belowGraphData = {},
  activeBtn = "Open",
  setActiveBtn,
  latestAmount = 0,
  showClsoeModal,
  showDeleteModal,
  onHide,
  setCloseTradeId,
  setQuantity,
  getGraphData,
}) => {
  const [tradesByTab, setTradesByTab] = useState({
    Open: [],
    Pending: [],
    Close: [],
  });

  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState(activeBtn);

  // ────────────────────────────────────────────────
  // When new data arrives from parent → process & cache per tab
  useEffect(() => {
    const rawTrades = belowGraphData?.data;

    if (!Array.isArray(rawTrades)) {
      setLoading(true);
      return;
    }
    

    const open = rawTrades.filter(t => t.order_status === "open" );
    const pending = rawTrades.filter(t => t.order_status === "pending" );
    const closed = rawTrades.filter(t => t.order_status === "close" );
console.log("daata",rawTrades)
    setTradesByTab({ Open: open, Pending: pending, Close: closed });
    setLoading(false);
    // Only now it's safe to show the current tab's content
    setCurrentTab(activeBtn);
  }, [belowGraphData, activeBtn]);

  // Very first mount — optional safety fetch
  useEffect(() => {
    if (loading && getGraphData) {
      getGraphData().catch(err => {
        console.error("Initial portfolio fetch failed", err);
        setLoading(false);
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const trades = tradesByTab[currentTab] || [];

  const tableContent = useMemo(() => {
    // During tab switch or when data is still loading → show loading
    if (loading || currentTab !== activeBtn) {
      return (
        <tr>
          <td colSpan={8} className="text-center py-20 text-gray-500">
            Loading {activeBtn.toLowerCase()} trades...
          </td>
        </tr>
      );
    }

    if (trades.length === 0) {
      return (
        <tr>
          <td colSpan={8} className="text-center py-20 text-gray-500">
            No {activeBtn.toLowerCase()} trades found
          </td>
        </tr>
      );
    }

    return trades.map((trade, index) => {
      const quantity =  trade.remaining_quantity || 0;
      const openPrice = quantity > 0 ? trade.amount / quantity : 0;
      const currentPrice = Number(latestAmount) || 0;
      const exitPrice = trade.close_amount != null ? Number(trade.close_amount) : currentPrice;
      let finalQuantity = 0;
      if(trade.order_status === "close"){
        finalQuantity = trade.remaining_quantity;
      }
      else{
        finalQuantity = trade.quantity
      }
      let pl = 0;
      if (activeBtn === "Open") {
        pl = trade.type === "buy"
          ? (currentPrice - openPrice) * trade.quantity
          : (openPrice - currentPrice) * trade.quantity;
      } else if (activeBtn === "Close") {
        pl = (exitPrice - openPrice) * trade.quantity;
      }

      const isProfit = pl >= 0;

      return (
        <tr key={trade.id || index} className="border-b border-gray-800 hover:bg-gray-800/50">
          <td className="py-2 px-4 text-white">{trade.Company?.name || "Unknown"}</td>

          <td className="py-2 px-4">
            <span
              className={`px-2 py-1 rounded-full text-xs font-semibold ${
                trade.type === "buy" ? "bg-green-900/50 text-green-400" : "bg-red-900/50 text-red-400"
              }`}
            >
              {trade.type?.toUpperCase() || "—"}
            </span>
          </td>

          <td className="py-2 px-4 text-center text-white">{trade.quantity}</td>

          <td className="py-2 px-4 text-right text-white">{openPrice.toFixed(2)}</td>

          <td className="py-2 px-4 text-right font-medium text-white">
            {exitPrice.toFixed(2)}
          </td>

          <td className="py-2 px-4 text-gray-400">
            {trade.createdAt ? new Date(trade.createdAt).toLocaleString("en-GB") : "—"}
          </td>

          {(activeBtn === "Open" || activeBtn === "Close") && (
            <td className={`py-2 px-4 text-right font-bold ${isProfit ? "text-green-400" : "text-red-400"}`}>
              {isProfit ? "+" : "−"}{Math.abs(pl).toFixed(6)}
            </td>
          )}

          <td className="py-2 px-4 text-center">
            {activeBtn === "Open" && (
              <button
                onClick={() => {
                  setQuantity(trade.quantity);
                  setCloseTradeId(trade.id);
                  showClsoeModal();
                  onHide?.();
                  getGraphData?.();
                }}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium"
              >
                Close
              </button>
            )}

            {activeBtn === "Pending" && (
              <button
                onClick={() => {
                  setCloseTradeId(trade.id);
                  showDeleteModal();
                  onHide?.();
                  getGraphData?.();
                }}
                className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-medium"
              >
                Cancel
              </button>
            )}

            {activeBtn === "Close" && (
              <span className="text-gray-500 text-sm font-medium">Closed</span>
            )}
          </td>
        </tr>
      );
    });
  }, [
    loading,
    trades,
    activeBtn,
    currentTab,
    latestAmount,
    setQuantity,
    setCloseTradeId,
    showClsoeModal,
    showDeleteModal,
    onHide,
    getGraphData,
  ]);

  return (
    <div className="portfolio-container bg-gray-900 rounded-xl shadow-2xl border border-gray-800 flex flex-col h-full overflow-hidden">
      {/* Tabs */}
      <div className="flex border-b border-gray-800 shrink-0">
        {["Open", "Pending", "Close"].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveBtn(tab)}
            className={`flex-1 py-3 px-5 text-sm font-medium transition-all duration-200
              ${activeBtn === tab
                ? "text-white bg-gradient-to-b from-blue-600 to-blue-700 border-b-4 border-blue-400"
                : "text-gray-400 hover:text-white hover:bg-gray-800/70"}`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Scrollable table */}
      <ScrollArea.Root className="flex-1 overflow-hidden">
        <ScrollArea.Viewport className="w-full h-full">
          <div className="min-w-max p-3">
            <table className="w-full text-sm table-auto">
              <thead className="sticky top-0 bg-gray-900 z-10 border-b border-gray-800">
                <tr className="text-gray-400 uppercase tracking-wider text-left text-xs">
                  <th className="py-3 px-4">Symbol</th>
                  <th className="py-3 px-4">Type</th>
                  <th className="py-3 px-4 text-center">Volume</th>
                  <th className="py-3 px-4 text-right">Open Price</th>
                  <th className="py-3 px-4 text-right">
                    {activeBtn === "Open" || activeBtn === "Pending" ? "Current" : "Exit"}
                  </th>
                  <th className="py-3 px-4">Time</th>
                  {(activeBtn === "Open" || activeBtn === "Close") && (
                    <th className="py-3 px-4 text-right">P/L ($)</th>
                  )}
                  <th className="py-3 px-4 text-center">Action</th>
                </tr>
              </thead>
              <tbody>{tableContent}</tbody>
            </table>
          </div>
        </ScrollArea.Viewport>

        <ScrollArea.Scrollbar orientation="vertical" className="w-2 bg-gray-800/60">
          <ScrollArea.Thumb className="bg-blue-600 rounded-full" />
        </ScrollArea.Scrollbar>

        <ScrollArea.Scrollbar orientation="horizontal" className="h-2 bg-gray-800/60">
          <ScrollArea.Thumb className="bg-blue-600 rounded-full" />
        </ScrollArea.Scrollbar>

        <ScrollArea.Corner />
      </ScrollArea.Root>
    </div>
  );
};

export default Portfolio;